import { useState } from "react";
import { useProposalT } from "../i18n/ProposalI18nContext";

export function ComparisonSection() {
  const { t } = useProposalT();
  const [activeTab, setActiveTab] = useState<"traditional" | "woba">(
    "traditional",
  );

  return (
    <section className="bg-[#FDFBF7] border-t border-[#E8E6E3] py-20 overflow-x-hidden">
      <div className="px-6 max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <div className="max-w-[95ch] mx-auto mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1A1814] leading-tight">
              {t("comparison.heading")}
            </h2>
          </div>
          <div className="max-w-[65ch] mx-auto">
            <p className="text-lg text-[#78736D]">
              {t("comparison.subheadingPre")}{" "}
              <strong>{t("comparison.subheadingBold")}</strong>{" "}
              {t("comparison.subheadingPost")}
            </p>
          </div>
        </div>

        {/* Tab switcher — shared between mobile and desktop */}
        <div className="flex gap-2 bg-white border border-white rounded-full mb-8 md:mb-24 p-2 shadow-[inset_0_3.72px_15px_-2.8px_rgba(0,0,0,0.25)] w-full max-w-[42rem] mx-auto">
          <button
            onClick={() => setActiveTab("traditional")}
            className={`flex-1 px-4 py-3 text-center font-medium transition-all duration-300 ${
              activeTab === "traditional"
                ? "bg-[#1A1814] text-white rounded-full shadow-[0_2px_8px_rgba(0,0,0,0.15)]"
                : "bg-transparent text-[rgba(0,0,0,0.4)]"
            }`}
          >
            {t("comparison.tabTraditional")}
          </button>
          <button
            onClick={() => setActiveTab("woba")}
            className={`flex-1 px-4 py-3 text-center font-medium transition-all duration-300 ${
              activeTab === "woba"
                ? "bg-[#f9e10d] text-black rounded-full font-bold shadow-[inset_0_3.72px_6.42px_5.58px_rgba(255,255,255,0.25)]"
                : "bg-transparent text-[rgba(0,0,0,0.4)]"
            }`}
          >
            {t("comparison.tabWoba")}
          </button>
        </div>

        {/* ── Desktop layout (md+) — original complex absolute-positioned design ── */}
        <div className="hidden md:flex flex-col items-center justify-center max-w-[80vh] mx-auto mb-16">
          <div className="w-full h-full relative">
            {activeTab === "traditional" ? (
              <TraditionalTabDesktop />
            ) : (
              <WobaTabDesktop />
            )}
          </div>
        </div>

        {/* ── Mobile layout — clean stacked design ── */}
        <div className="md:hidden">
          {activeTab === "traditional" ? (
            <TraditionalTabMobile />
          ) : (
            <WobaTabMobile />
          )}
        </div>
      </div>
    </section>
  );
}

/* ─── Mobile versions ──────────────────────────────────────────────── */

const INVOICE_IMAGES = [
  {
    src: "https://d1y4va1nna2r1p.cloudfront.net/spaces/db194906-5587-46ac-9a64-90694123851e.svg",
    rotate: 12,
    top: 4,
  },
  {
    src: "https://d1y4va1nna2r1p.cloudfront.net/spaces/069557c4-90b0-4ee9-9eb8-14f11aa6f061.svg",
    rotate: -8,
    top: 0,
  },
  {
    src: "https://d1y4va1nna2r1p.cloudfront.net/spaces/52bed4d5-ab7a-449f-999a-a1fafcfdc465.svg",
    rotate: -9,
    top: 6,
  },
  {
    src: "https://d1y4va1nna2r1p.cloudfront.net/spaces/7ff1dca8-2984-49d2-8b6d-4c848f74aeaa.svg",
    rotate: 2,
    top: 2,
  },
  {
    src: "https://d1y4va1nna2r1p.cloudfront.net/spaces/6dfe59b2-cb13-4c39-9881-77974605d823.svg",
    rotate: -5,
    top: 0,
  },
  {
    src: "https://d1y4va1nna2r1p.cloudfront.net/spaces/205f9d17-a4a2-49b3-9486-96a6ccd85bf2.svg",
    rotate: 7,
    top: 5,
  },
];

function TraditionalTabMobile() {
  const { t } = useProposalT();
  return (
    <div className="flex flex-col items-center gap-5 pb-4">
      <div className="w-full max-w-xs mx-auto">
        <img
          loading="lazy"
          src="https://d1y4va1nna2r1p.cloudfront.net/spaces/b2f72b4a-2e87-43b9-af0c-49df16cf0808.webp"
          alt="Escritório tradicional"
          className="w-full h-auto object-contain"
        />
      </div>

      {/* Floating invoices strip */}
      <div className="relative w-full h-28 overflow-hidden">
        {INVOICE_IMAGES.map((inv, i) => (
          <div
            key={i}
            className="absolute w-14 h-[4.5rem] backdrop-blur-[5px] bg-white/80 border border-white/50 rounded-lg overflow-hidden shadow-[5px_6px_17px_rgba(122,122,122,0.15)]"
            style={{
              left: `${i * 16 + 1}%`,
              top: `${inv.top * 4}px`,
              transform: `rotate(${inv.rotate}deg)`,
              zIndex: i % 2 === 0 ? 2 : 3,
            }}
          >
            <img
              loading="lazy"
              src={inv.src}
              alt="Boleto"
              className="w-full h-full object-cover"
            />
          </div>
        ))}
      </div>

      <div
        className="w-full rounded-2xl p-6"
        style={{
          backdropFilter: "blur(5px)",
          background: "rgba(255,255,255,0.85)",
          border: "1px solid rgba(255,255,255,0.5)",
          boxShadow: "5px 6px 17px rgba(122,122,122,0.1)",
        }}
      >
        <h2 className="text-xl font-semibold text-[#1A1814] mb-2">
          {t("comparison.traditional.title")}
        </h2>
        <p className="text-sm text-[#78736D] leading-relaxed">
          {t("comparison.traditional.desc")}
        </p>
      </div>
    </div>
  );
}

function WobaTabMobile() {
  const { t } = useProposalT();
  return (
    <div className="flex flex-col items-center gap-5 pb-4">
      <div className="w-full max-w-xs mx-auto">
        <img
          loading="lazy"
          src="https://d1y4va1nna2r1p.cloudfront.net/spaces/e5f7a21c-5abf-43fb-b250-07c7933179cf.webp"
          alt="Escritório Woba"
          className="w-full h-auto object-contain"
        />
      </div>

      {/* Woba document card */}
      <div className="flex justify-center w-full">
        <div className="relative">
          <div className="w-20 h-24 backdrop-blur-[5px] bg-white/80 border border-white/50 rounded-lg overflow-hidden shadow-[5px_6px_17px_rgba(122,122,122,0.15)] -rotate-[4deg]">
            <img
              loading="lazy"
              src="https://d1y4va1nna2r1p.cloudfront.net/spaces/4793ed9a-4041-40de-845d-5953c89840b5.svg"
              alt="Documento Woba"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute -top-2 -right-2 w-5 h-5">
            <img
              loading="lazy"
              src="https://d1y4va1nna2r1p.cloudfront.net/spaces/08c94da4-c2fe-4ae1-ab8d-c6334069fc38.svg"
              alt="Estrelas"
              className="w-full h-full object-contain"
            />
          </div>
        </div>
      </div>

      <div
        className="w-full rounded-2xl p-6"
        style={{
          backdropFilter: "blur(5px)",
          background: "rgba(255,255,255,0.85)",
          border: "1px solid rgba(255,255,255,0.5)",
          boxShadow: "5px 6px 17px rgba(122,122,122,0.1)",
        }}
      >
        <h2 className="text-xl font-semibold text-[#1A1814] mb-2">
          {t("comparison.woba.title")}
        </h2>
        <p className="text-sm text-[#78736D] leading-relaxed">
          {t("comparison.woba.desc")}
        </p>
      </div>
    </div>
  );
}

/* ─── Desktop versions (original code) ─────────────────────────────── */

function TraditionalTabDesktop() {
  const { t } = useProposalT();
  return (
    <div className="w-full h-full relative flex items-center justify-center">
      <div className="absolute top-[-8vh] right-[26vh] rotate-[12deg] w-[12vh] h-[15vh] backdrop-blur-[5px] bg-white/70 border border-white/50 rounded-lg overflow-hidden shadow-[5px_6px_17px_rgba(122,122,122,0.1),19px_24px_30px_rgba(122,122,122,0.09),44px_53px_41px_rgba(122,122,122,0.05),78px_94px_49px_rgba(122,122,122,0.01)]">
        <img
          loading="lazy"
          src="https://d1y4va1nna2r1p.cloudfront.net/spaces/db194906-5587-46ac-9a64-90694123851e.svg"
          alt="Boleto"
          className="w-full h-full object-cover"
        />
      </div>

      <div className="absolute top-[-4vh] right-[17vh] -rotate-[9deg] w-[12vh] h-[15vh] backdrop-blur-[5px] bg-white/70 border border-white/50 rounded-lg overflow-hidden shadow-[5px_6px_17px_rgba(122,122,122,0.1),19px_24px_30px_rgba(122,122,122,0.09),44px_53px_41px_rgba(122,122,122,0.05),78px_94px_49px_rgba(122,122,122,0.01)] z-[6]">
        <img
          loading="lazy"
          src="https://d1y4va1nna2r1p.cloudfront.net/spaces/52bed4d5-ab7a-449f-999a-a1fafcfdc465.svg"
          alt="Boleto"
          className="w-full h-full object-cover"
        />
      </div>

      <div className="absolute top-[-8vh] right-[7vh] rotate-[1deg] w-[12vh] h-[15vh] backdrop-blur-[5px] bg-white/70 border border-white/50 rounded-lg overflow-hidden shadow-[5px_6px_17px_rgba(122,122,122,0.1),19px_24px_30px_rgba(122,122,122,0.09),44px_53px_41px_rgba(122,122,122,0.05),78px_94px_49px_rgba(122,122,122,0.01)]">
        <img
          loading="lazy"
          src="https://d1y4va1nna2r1p.cloudfront.net/spaces/7ff1dca8-2984-49d2-8b6d-4c848f74aeaa.svg"
          alt="Boleto"
          className="w-full h-full object-cover"
        />
      </div>

      <div className="absolute top-[4vh] right-[5vh] rotate-[4deg] w-[12vh] h-[15vh] backdrop-blur-[5px] bg-white/70 border border-white/50 rounded-lg overflow-hidden shadow-[5px_6px_17px_rgba(122,122,122,0.1),19px_24px_30px_rgba(122,122,122,0.09),44px_53px_41px_rgba(122,122,122,0.05),78px_94px_49px_rgba(122,122,122,0.01)]">
        <img
          loading="lazy"
          src="https://d1y4va1nna2r1p.cloudfront.net/spaces/205f9d17-a4a2-49b3-9486-96a6ccd85bf2.svg"
          alt="Boleto"
          className="w-full h-full object-cover"
        />
      </div>

      <div className="absolute top-[-8vh] right-[37vh] -rotate-[6deg] w-[12vh] h-[15vh] backdrop-blur-[5px] bg-white/70 border border-white/50 rounded-lg overflow-hidden shadow-[5px_6px_17px_rgba(122,122,122,0.1),19px_24px_30px_rgba(122,122,122,0.09),44px_53px_41px_rgba(122,122,122,0.05),78px_94px_49px_rgba(122,122,122,0.01)] z-[2]">
        <img
          loading="lazy"
          src="https://d1y4va1nna2r1p.cloudfront.net/spaces/6dfe59b2-cb13-4c39-9881-77974605d823.svg"
          alt="Boleto"
          className="w-full h-full object-cover"
        />
      </div>

      <div className="absolute top-[-8vh] left-[20vh] rotate-[4deg] w-[12vh] h-[15vh] backdrop-blur-[5px] bg-white/70 border border-white/50 rounded-lg overflow-hidden shadow-[5px_6px_17px_rgba(122,122,122,0.1),19px_24px_30px_rgba(122,122,122,0.09),44px_53px_41px_rgba(122,122,122,0.05),78px_94px_49px_rgba(122,122,122,0.01)]">
        <img
          loading="lazy"
          src="https://d1y4va1nna2r1p.cloudfront.net/spaces/069557c4-90b0-4ee9-9eb8-14f11aa6f061.svg"
          alt="Boleto"
          className="w-full h-full object-cover"
        />
      </div>

      <div className="absolute top-[-2vh] left-[11vh] -rotate-[22deg] w-[12vh] h-[15vh] backdrop-blur-[5px] bg-white/70 border border-white/50 rounded-lg overflow-hidden shadow-[5px_6px_17px_rgba(122,122,122,0.1),19px_24px_30px_rgba(122,122,122,0.09),44px_53px_41px_rgba(122,122,122,0.05),78px_94px_49px_rgba(122,122,122,0.01)]">
        <img
          loading="lazy"
          src="https://d1y4va1nna2r1p.cloudfront.net/spaces/fc6ad000-1142-4d21-b054-a2ed3d145835.svg"
          alt="Boleto"
          className="w-full h-full object-cover"
        />
      </div>

      <div className="absolute top-[7vh] left-[2vh] -rotate-[9deg] w-[12vh] h-[15vh] backdrop-blur-[5px] bg-white/70 border border-white/50 rounded-lg overflow-hidden shadow-[5px_6px_17px_rgba(122,122,122,0.1),19px_24px_30px_rgba(122,122,122,0.09),44px_53px_41px_rgba(122,122,122,0.05),78px_94px_49px_rgba(122,122,122,0.01)] z-[6]">
        <img
          loading="lazy"
          src="https://d1y4va1nna2r1p.cloudfront.net/spaces/e584e83d-4b23-4b08-9267-d1b5f973447c.svg"
          alt="Boleto"
          className="w-full h-full object-cover"
        />
      </div>

      <div className="absolute top-[22.7vh] left-[3.9vh] -rotate-[14deg] w-[12vh] h-[15vh] backdrop-blur-[5px] bg-white/70 border border-white/50 rounded-lg overflow-hidden shadow-[5px_6px_17px_rgba(122,122,122,0.1),19px_24px_30px_rgba(122,122,122,0.09),44px_53px_41px_rgba(122,122,122,0.05),78px_94px_49px_rgba(122,122,122,0.01)] z-[6]">
        <img
          loading="lazy"
          src="https://d1y4va1nna2r1p.cloudfront.net/spaces/50516bf6-cd07-494f-837f-409d99369e6b.svg"
          alt="Boleto"
          className="w-full h-full object-cover"
        />
      </div>

      <div className="absolute top-[30.9vh] left-[15.6vh] rotate-0 w-[12vh] h-[15vh] backdrop-blur-[5px] bg-white/70 border border-white/50 rounded-lg overflow-hidden shadow-[5px_6px_17px_rgba(122,122,122,0.1),19px_24px_30px_rgba(122,122,122,0.09),44px_53px_41px_rgba(122,122,122,0.05),78px_94px_49px_rgba(122,122,122,0.01)] z-[8]">
        <img
          loading="lazy"
          src="https://d1y4va1nna2r1p.cloudfront.net/spaces/da7b35fa-23dd-41cb-85ca-17e590a55ed9.svg"
          alt="Boleto"
          className="w-full h-full object-cover"
        />
      </div>

      <div className="absolute top-[38vh] left-[24.5vh] rotate-[18deg] w-[12vh] h-[15vh] backdrop-blur-[5px] bg-white/70 border border-white/50 rounded-lg overflow-hidden shadow-[5px_6px_17px_rgba(122,122,122,0.1),19px_24px_30px_rgba(122,122,122,0.09),44px_53px_41px_rgba(122,122,122,0.05),78px_94px_49px_rgba(122,122,122,0.01)] z-[7]">
        <img
          loading="lazy"
          src="https://d1y4va1nna2r1p.cloudfront.net/spaces/c29c058b-7e42-4c93-bf0f-3c57a319bd4c.svg"
          alt="Boleto"
          className="w-full h-full object-cover"
        />
      </div>

      <div className="absolute top-[37vh] left-[36.9vh] -rotate-[9deg] w-[12vh] h-[15vh] backdrop-blur-[5px] bg-white/70 border border-white/50 rounded-lg overflow-hidden shadow-[5px_6px_17px_rgba(122,122,122,0.1),19px_24px_30px_rgba(122,122,122,0.09),44px_53px_41px_rgba(122,122,122,0.05),78px_94px_49px_rgba(122,122,122,0.01)] z-[6]">
        <img
          loading="lazy"
          src="https://d1y4va1nna2r1p.cloudfront.net/spaces/e86361c9-098c-48d8-9901-6c2fbc56ce2b.svg"
          alt="Boleto"
          className="w-full h-full object-cover"
        />
      </div>

      <div className="absolute top-[35vh] right-[21vh] rotate-[6deg] w-[12vh] h-[15vh] backdrop-blur-[5px] bg-white/70 border border-white/50 rounded-lg overflow-hidden shadow-[5px_6px_17px_rgba(122,122,122,0.1),19px_24px_30px_rgba(122,122,122,0.09),44px_53px_41px_rgba(122,122,122,0.05),78px_94px_49px_rgba(122,122,122,0.01)] z-[20]">
        <img
          loading="lazy"
          src="https://d1y4va1nna2r1p.cloudfront.net/spaces/61be224d-c6e5-4666-b692-fdc5f30d9a44.svg"
          alt="Boleto"
          className="w-full h-full object-cover"
        />
      </div>

      <div className="absolute bottom-0 right-[-20vh] z-[8] max-w-[23em] backdrop-blur-[5px] bg-white/70 border border-white/50 rounded-2xl p-8 flex flex-col items-start justify-center text-[0.8vw] shadow-[5px_6px_17px_rgba(122,122,122,0.1),19px_24px_30px_rgba(122,122,122,0.09),44px_53px_41px_rgba(122,122,122,0.05),78px_94px_49px_rgba(122,122,122,0.01)]">
        <h2 className="text-2xl font-medium mb-2">
          {t("comparison.traditional.title")}
        </h2>
        <p className="text-base leading-[1.15] max-w-[14em]">
          {t("comparison.traditional.desc")}
        </p>
      </div>

      <div className="relative z-[5] w-full max-w-[65vh] h-full pointer-events-none">
        <img
          loading="lazy"
          src="https://d1y4va1nna2r1p.cloudfront.net/spaces/b2f72b4a-2e87-43b9-af0c-49df16cf0808.webp"
          alt="Isometrico Sala"
          className="w-full h-full object-contain"
        />
      </div>
    </div>
  );
}

function WobaTabDesktop() {
  const { t } = useProposalT();
  return (
    <div className="w-full h-full relative flex items-center justify-center">
      <div className="absolute top-[-2.5rem] left-24 w-[12vh] h-[15vh] backdrop-blur-[5px] bg-white/70 border border-white/50 rounded-lg shadow-[5px_6px_17px_rgba(122,122,122,0.1),19px_24px_30px_rgba(122,122,122,0.09),44px_53px_41px_rgba(122,122,122,0.05),78px_94px_49px_rgba(122,122,122,0.01)] z-[6]">
        <img
          loading="lazy"
          src="https://d1y4va1nna2r1p.cloudfront.net/spaces/4793ed9a-4041-40de-845d-5953c89840b5.svg"
          alt="Boleto"
          className="w-full h-full object-cover"
        />
        <div className="absolute top-[-2vh] right-0 w-[2.5vh]">
          <img
            loading="lazy"
            src="https://d1y4va1nna2r1p.cloudfront.net/spaces/08c94da4-c2fe-4ae1-ab8d-c6334069fc38.svg"
            alt="Estrelas"
            className="w-full h-full object-contain"
          />
        </div>
      </div>

      <div className="absolute bottom-0 right-[-4vh] z-[8] max-w-[25em] backdrop-blur-[5px] bg-white/70 border border-white/50 rounded-lg p-6 flex flex-col items-start justify-center text-[0.8vw] shadow-[5px_6px_17px_rgba(122,122,122,0.1),19px_24px_30px_rgba(122,122,122,0.09),44px_53px_41px_(rgba(122,122,122,0.05)),78px_94px_49px_rgba(122,122,122,0.01)]">
        <h2 className="text-2xl font-medium mb-2 max-w-[25ch]">
          {t("comparison.woba.title")}
        </h2>
        <p className="text-base leading-[1.15] max-w-[25ch]">
          {t("comparison.woba.desc")}
        </p>
      </div>

      <div className="relative z-[5] w-full max-w-[65vh] h-full pointer-events-none">
        <img
          loading="lazy"
          src="https://d1y4va1nna2r1p.cloudfront.net/spaces/e5f7a21c-5abf-43fb-b250-07c7933179cf.webp"
          alt="Isometrico Sala"
          className="w-full h-full object-contain"
        />
      </div>
    </div>
  );
}
